import { plural } from '../../../../shared/sso-workspace/format'

// A target type's one line on the "What kind of target?" step: the server's
// own description when it sends one, else the admin's words.
export const TYPE_WORDS = {
  email: 'Email a list of addresses.',
  slack_app: 'Post to a channel in a connected Slack workspace.',
  webhook: 'Send each alert as a signed JSON request to a URL.',
  slack: 'Post through a webhook link you copy from Slack.'
}
export const typeHelp = at => (at && (at.description || TYPE_WORDS[at.type])) || ''

// A type's name where the admin says it better than the catalogue: the
// advanced Slack type sits under an "Advanced" heading, so its name needn't
// say so (owner 2026-10-30; core's label is "Slack (advanced: paste a webhook
// link)").
export const TYPE_TITLES = { slack: 'Slack incoming webhook' }
export const typeTitle = at => (at && (TYPE_TITLES[at.type] || at.label || at.type)) || ''
// The same name inside a sentence ("New email target", "New Slack target"):
// lower case, except a product's name.
export const typeNoun = at => {
  const title = typeTitle(at)
  return /^Slack/.test(title) ? title : title.charAt(0).toLowerCase() + title.slice(1)
}

// Where a target's alerts go, in words: "#ops" (a Slack channel), "2
// addresses" (email), "hooks.example.org" (a webhook's host), never a
// delivery's internal destination ("target:7").
export const whereOf = (target, sentDeliveries) => {
  if (!target) return ''
  const config = target.config || {}
  if (config.channel_name) return `#${config.channel_name}`
  if (target.type === 'email') {
    const each = config.email_mode !== 'together'
    const count = each && sentDeliveries !== undefined ? sentDeliveries : (target.addresses || []).length
    return plural(count, 'address', 'addresses')
  }
  return target.host || ''
}

const failedStatus = s => s === 'failed' || s === 'expired'
// A delivery's own reason, already plain words (the provider's answer).
export const deliveryReason = d => (d && (d.lastError || ((d.history || []).find(h => h.error) || {}).error)) || ''

// A test's answer as one status line (owner 2026-10-30: "target:7: sent ·
// ok" in a box "looks terrible"): sent, with where; the delivery's own reason
// when it failed; or why nothing was sent.
export const testOutcome = (run, target) => {
  const deliveries = (run && run.deliveries) || []
  const alertID = run && run.alert && run.alert.id
  if (!deliveries.length) return { tone: 'warn', text: 'Nothing was sent: the target is off, or has nowhere to send yet.', alertID }
  const failed = deliveries.filter(d => failedStatus(d.status))
  if (failed.length) return { tone: 'bad', text: deliveryReason(failed[0]) || "The test couldn't be sent.", alertID }
  const sent = deliveries.filter(d => d.status === 'sent')
  const where = whereOf(target, sent.length)
  if (sent.length) return { tone: 'ok', text: where ? `Sent to ${where} just now` : 'Sent just now', alertID }
  return { tone: 'info', text: where ? `Queued for ${where}` : 'Queued', alertID }
}

// Why a target can't be deleted: a route uses it (the server refuses it too).
export const deleteBlocked = usedBy => (usedBy > 0
  ? `${plural(usedBy, 'route')} ${usedBy === 1 ? 'uses' : 'use'} this target. Take it off ${usedBy === 1 ? 'that route' : 'those routes'} to delete it.`
  : null)

// The personal data switch (owner 2026-10-30: help says what the option
// does, the same on or off). Email targets are staff inboxes and get what they
// get today (spec D25), so they have no switch.
export const PERSONAL_HELP = "Include names, email addresses and message text in this target's alerts."
export const PERSONAL_WARNING = 'They leave EducationXR, staff names included. The change is recorded in the audit log.'
export const takesPersonalSwitch = type => type !== 'email'

// A target's page subtitle, its type and where it sends: "Slack ·
// #edxr-dev-mon-errors · Heizenrader", "Email · 3 addresses", "Webhook ·
// hooks.example.org". An organization's target names it last.
export const targetSubtitle = (target, at) => {
  const config = target.config || {}
  const where = target.type === 'slack_app'
    ? [config.channel_name && `#${config.channel_name}`, target.connection && target.connection.externalName]
    : target.type === 'email'
      ? [plural((target.addresses || []).length, 'address', 'addresses')]
      : [target.host]
  const org = target.scopeType === 'organization' ? target.organizationName || `Organization ${target.organizationID}` : null
  return [typeTitle(at) || target.type, ...where, org].filter(Boolean).join(' · ')
}

// A required write-only secret that was never set: the target can't send.
export const missingSecret = (target, at) =>
  ((at && at.configSchema) || []).some(f => f.secret && f.required && !((target.secrets || {})[f.key] || {}).set)

const failingNow = t => Boolean(t.lastFailureAt) && (!t.lastSuccessAt || Date.parse(t.lastFailureAt) > Date.parse(t.lastSuccessAt))

// A target's status pill: Off; its workspace's state when that isn't
// connected; nowhere to send; failing since its last success; else Active.
// section: the page section that explains it.
export const targetStatus = (target, at, connectionLabel = s => s) => {
  if (target.enabled === false) return { key: 'off', label: 'Off', tone: null }
  const c = target.connection
  if (c && c.status !== 'connected') {
    const s = connectionLabel(c.status)
    return { key: 'connection', label: s.label, tone: s.tone, reason: c.externalName ? `${c.externalName}: ${s.label.toLowerCase()}` : s.label, section: 'details' }
  }
  if (target.type === 'email' && !(target.addresses || []).length) return { key: 'empty', label: 'No addresses', tone: 'warn', section: 'addresses' }
  if (missingSecret(target, at)) return { key: 'unset', label: 'Not set up', tone: 'warn', section: 'secrets' }
  if (failingNow(target)) return { key: 'failing', label: 'Failing', tone: 'bad', reason: target.lastError || 'The last delivery failed.', section: 'deliveries' }
  return { key: 'active', label: 'Active', tone: 'ok' }
}

// The personal data tile, in words.
export const personalTile = target => (target.type === 'email'
  ? { value: 'Included', detail: 'staff inboxes get every field' }
  : target.allowPersonal
    ? { value: 'Allowed', detail: 'names, addresses and messages go out' }
    : { value: 'Not allowed', detail: 'left out of its alerts' })

export const isFailing = failingNow
